import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import ContentCard from "./ContentCard";
import ViewModal from "./ViewModal";
import CommentsPanel from "./CommentsPanel";
import BoardSelector from "./BoardSelector";
import AddDropdown from "./AddDropdown";
import { Search, X, Sun, Moon, SlidersHorizontal, ChevronDown } from "lucide-react";

const FilterPill = ({ label, value, onRemove, color }) => (
  <div className={`flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full text-[11px] font-medium transition-all ${color || 'bg-violet-500/20 text-violet-300 border border-violet-500/30'}`}>
    <span>{label}: {value}</span>
    <button 
      onClick={onRemove}
      className="w-4 h-4 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all"
    >
      <X size={10} />
    </button>
  </div>
);

const FilterDropdown = ({ label, options, selected, onSelect, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-[12px] font-medium transition-all border ${
          selected.length > 0 
            ? 'bg-violet-500/10 border-violet-500/30 text-violet-300' 
            : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:text-white/70 hover:border-white/[0.15]'
        }`}
      >
        {Icon && <Icon size={13} />}
        {label}
        {selected.length > 0 && (
          <span className="w-5 h-5 rounded-full bg-violet-500/30 text-violet-200 text-[10px] flex items-center justify-center">{selected.length}</span>
        )}
        <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-2 min-w-[180px] bg-[#1a1a1e] border border-white/[0.1] rounded-xl shadow-2xl z-50 py-2 max-h-[300px] overflow-y-auto backdrop-blur-xl">
            {options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  onSelect(opt.value);
                }}
                className={`w-full flex items-center gap-2.5 px-4 py-2 text-[12px] text-left transition-all ${
                  selected.includes(opt.value) 
                    ? 'bg-violet-500/20 text-white' 
                    : 'text-white/60 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                {opt.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: opt.color }} />}
                {opt.label}
                {selected.includes(opt.value) && (
                  <div className="ml-auto w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center">
                    <X size={10} className="text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ContentGrid = ({ refreshKey, onRefresh, filterType, categories, tags, onTagsChange, onCategoriesChange }) => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [commentsItem, setCommentsItem] = useState(null);
  const [boardItem, setBoardItem] = useState(null);
  const [isDark, setIsDark] = useState(true);
  const [activeFilters, setActiveFilters] = useState({ platform: [], tags: [], category: [], type: [] });

  useEffect(() => {
    setLoading(true);
    const params = filterType ? { content_type: filterType } : {};
    api.get("/content", { params })
      .then((res) => setItems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, refreshKey, filterType]);

  const filtered = items.filter((item) => {
    if (search && !item.title.toLowerCase().includes(search.toLowerCase()) &&
        !item.tags?.some((t) => t.toLowerCase().includes(search.toLowerCase())) &&
        !item.platform?.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeFilters.platform.length > 0 && !activeFilters.platform.includes(item.platform)) return false;
    if (activeFilters.tags.length > 0 && !activeFilters.tags.some((t) => item.tags?.includes(t))) return false;
    if (activeFilters.category.length > 0 && !activeFilters.category.includes(item.category_id)) return false;
    if (activeFilters.type.length > 0 && !activeFilters.type.includes(item.content_type)) return false;
    return true;
  });

  const handleDelete = async (id) => {
    try {
      await api.delete(`/content/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      onRefresh?.();
    } catch {}
  };

  const handleCopyLink = (url) => navigator.clipboard.writeText(url || "");

  const getCategoryColor = (categoryId) => {
    if (!categoryId) return null;
    const cat = categories?.find((c) => c.id === categoryId);
    return cat?.color || null;
  };

  const typeLabels = { ad: "Ads", landing_page: "Landing Pages", ugc: "UGC & Reels", email: "Emails" };
  
  const platformOptions = ["Facebook", "Instagram", "TikTok", "LinkedIn", "YouTube", "Email", "Web"].map(p => ({ value: p, label: p }));
  const typeOptions = [
    { value: "ad", label: "Ads" },
    { value: "landing_page", label: "Landing Pages" },
    { value: "ugc", label: "UGC" },
    { value: "email", label: "Emails" },
  ];
  const categoryOptions = categories?.map(c => ({ value: c.id, label: c.name, color: c.color })) || [];

  const toggleFilter = (type, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [type]: prev[type].includes(value) 
        ? prev[type].filter(v => v !== value)
        : [...prev[type], value]
    }));
  };

  const activeFilterPills = [
    ...activeFilters.platform.map(p => ({ type: 'platform', value: p, label: 'Platform' })),
    ...activeFilters.type.map(t => ({ type: 'type', value: t, label: 'Type' })),
    ...activeFilters.category.map(c => {
      const cat = categories?.find(cat => cat.id === c);
      return { type: 'category', value: c, label: 'Catégorie', displayValue: cat?.name || c };
    }),
  ];

  return (
    <div className={`min-h-screen p-8 transition-colors duration-500 ${isDark ? 'bg-[#0a0a0c]' : 'bg-[#f5f5f7]'}`}>
      {/* Noise texture overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.015] z-0" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between mb-8">
        <div>
          <h1 className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {filterType ? typeLabels[filterType] || "Content" : "All Content"}
          </h1>
          <p className={`text-[13px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            {filtered.length} éléments
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Dark/Light toggle */}
          <button
            onClick={() => setIsDark(!isDark)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
              isDark 
                ? 'bg-white/[0.05] text-white/50 hover:text-white hover:bg-white/[0.1]' 
                : 'bg-gray-100 text-gray-500 hover:text-gray-700 hover:bg-gray-200'
            }`}
            title={isDark ? "Mode clair" : "Mode sombre"}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          
          <AddDropdown 
            categories={categories} 
            tags={tags}
            onRefresh={onRefresh}
            onCategoriesChange={onCategoriesChange}
            onTagsChange={onTagsChange}
            isDark={isDark}
          />
        </div>
      </div>

      {/* Search and Filters */}
      <div className="relative z-10 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Glassmorphism Search */}
          <div className={`relative flex-1 max-w-md transition-all duration-300 ${searchFocused ? 'scale-[1.01]' : ''}`}>
            <div className={`absolute inset-0 rounded-xl transition-all duration-300 ${
              searchFocused 
                ? 'bg-violet-500/10 shadow-[0_0_20px_rgba(139,92,246,0.15)]' 
                : ''
            }`} />
            <Search 
              size={16} 
              className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${
                searchFocused 
                  ? 'text-violet-400 scale-110' 
                  : isDark ? 'text-white/30' : 'text-gray-400'
              }`} 
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Rechercher par titre, tag, plateforme..."
              className={`relative w-full rounded-xl pl-11 pr-4 py-3 text-[13px] transition-all duration-300 ${
                isDark 
                  ? 'bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-white/30 focus:border-violet-500/50 focus:bg-white/[0.06]' 
                  : 'bg-white border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-violet-500 focus:shadow-lg'
              } focus:outline-none backdrop-blur-xl`}
              data-testid="search-input"
            />
          </div>

          {/* Filter Dropdowns */}
          <FilterDropdown 
            label="Platform" 
            options={platformOptions}
            selected={activeFilters.platform}
            onSelect={(v) => toggleFilter('platform', v)}
            icon={SlidersHorizontal}
          />
          <FilterDropdown 
            label="Type" 
            options={typeOptions}
            selected={activeFilters.type}
            onSelect={(v) => toggleFilter('type', v)}
          />
          {categoryOptions.length > 0 && (
            <FilterDropdown 
              label="Catégorie" 
              options={categoryOptions}
              selected={activeFilters.category}
              onSelect={(v) => toggleFilter('category', v)}
            />
          )}
        </div>

        {/* Active Filter Pills */}
        {activeFilterPills.length > 0 && (
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <span className={`text-[11px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Filtres actifs:</span>
            {activeFilterPills.map((pill, i) => (
              <FilterPill 
                key={i}
                label={pill.label}
                value={pill.displayValue || pill.value}
                onRemove={() => toggleFilter(pill.type, pill.value)}
              />
            ))}
            <button
              onClick={() => setActiveFilters({ platform: [], tags: [], category: [], type: [] })}
              className="text-[11px] text-white/40 hover:text-white/70 transition-colors ml-2"
            >
              Tout effacer
            </button>
          </div>
        )}
      </div>

      {/* Masonry Grid */}
      <div className="relative z-10">
        {loading ? (
          <div className="columns-2 lg:columns-3 xl:columns-4 2xl:columns-5 gap-5 space-y-5">
            {[...Array(10)].map((_, i) => (
              <div 
                key={i} 
                className={`rounded-2xl h-64 animate-pulse break-inside-avoid ${
                  isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-100 border border-gray-200'
                }`} 
                style={{ height: `${200 + Math.random() * 150}px` }}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
              isDark ? 'bg-white/[0.05]' : 'bg-gray-100'
            }`}>
              <Search size={24} className={isDark ? 'text-white/20' : 'text-gray-300'} />
            </div>
            <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Aucun contenu trouvé</p>
            <p className={`text-[12px] mt-1 ${isDark ? 'text-white/20' : 'text-gray-400'}`}>
              Essayez de modifier vos filtres ou d'ajouter du contenu
            </p>
          </div>
        ) : (
          <div className="columns-2 lg:columns-3 xl:columns-4 2xl:columns-5 gap-5 space-y-5">
            {filtered.map((item) => (
              <div key={item.id} className="break-inside-avoid">
                <ContentCard
                  item={item}
                  onView={() => setViewItem(item)}
                  onComments={() => setCommentsItem(item)}
                  onBoard={() => setBoardItem(item)}
                  onCopyLink={() => handleCopyLink(item.source_url)}
                  onDelete={() => handleDelete(item.id)}
                  categoryColor={getCategoryColor(item.category_id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {viewItem && <ViewModal item={viewItem} onClose={() => setViewItem(null)} categories={categories} onDelete={() => handleDelete(viewItem.id)} />}
      {commentsItem && <CommentsPanel item={commentsItem} onClose={() => setCommentsItem(null)} />}
      {boardItem && <BoardSelector item={boardItem} onClose={() => setBoardItem(null)} onDone={onRefresh} />}
    </div>
  );
};

export default ContentGrid;
